import React from "react";

import {
  CheckBox,
  PrimaryButton,
  SecondaryButton,
  MultipleSelect,
  SearchField,
  ErrorMessage,
  SingleSelect,
} from "widgets";
import {
  useFetchPackages,
  GRADES,
  TARGET_EXAMS,
  useVerticalAndTenantsId,
} from "store/backend/assets";
import Loader from "Shared/Loader/Loader";
import SelectedPackageSubscriptions from "components/OrderSummary/SelectedPackageSubscriptions";
import { INR_SYMBOL, KH_VERTICAL_ID } from "constants/config";
import { toast } from "react-fox-toast";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";

import styles from "./PackageSubscription.module.scss";

const handlePackageSelection = (
  packageSubscription,
  selectedPackageSubscriptions,
  onPackagesSelected,
  filters
) => {
  const isAlreadySelected = selectedPackageSubscriptions.some(
    (p) => p.id === packageSubscription.id
  );

  if (isAlreadySelected) {
    const updatedSelections = selectedPackageSubscriptions.filter(
      (p) => p.id !== packageSubscription.id
    );
    onPackagesSelected(updatedSelections);
    return;
  }
  if (filters.selectedVerticalId === KH_VERTICAL_ID) {
    if (selectedPackageSubscriptions.length >= 1) {
      toast.warning("Only one package can be selected for this vertical!", {
        position: "top-center",
      });
      return;
    }
  }
  onPackagesSelected([...selectedPackageSubscriptions, packageSubscription]);
};

const handleOnClickRefreshButton = (onFilterChange) => {
  onFilterChange("selectedGrade", []);
  onFilterChange("selectedTargetExam", []);
};

const handleOnClickSearchButton = (refetch) => {
  refetch();
};

const isSearchButtonEnabled = (
  selectedTargetExam,
  selectedGrade,
  selectedVerticalId,
  selectedTenantId
) =>
  selectedTargetExam.length > 0 &&
  selectedGrade.length > 0 &&
  selectedVerticalId !== null &&
  selectedTenantId !== null;

const handleTenantChange = (event, onFilterChange, tenantMenuItems) => {
  const newTenantId = event.target.value;
  const selectedTenant = tenantMenuItems.find(
    (tenant) => tenant.value === newTenantId
  );
  if (selectedTenant) {
    onFilterChange("selectedTenantId", newTenantId);
    onFilterChange("selectedOfficeLocationId", selectedTenant.state_id);
  }
};

const renderPackageDetails = (
  packageSubscription,
  selectedPackageSubscriptions,
  onPackagesSelected,
  filters
) => {
  return (
    <div key={packageSubscription.id} className={styles.packageDetailBox}>
      <CheckBox
        className={styles.packageCheckBox}
        checked={selectedPackageSubscriptions.some(
          (p) => p.id === packageSubscription.id
        )}
        handleCheck={() =>
          handlePackageSelection(
            packageSubscription,
            selectedPackageSubscriptions,
            onPackagesSelected,
            filters
          )
        }
      />
      <div className={styles.packageDetailContent}>
        <div className={styles.label}>
          <span className={styles.detailTitle}>Package Name:</span>
          <span>{packageSubscription.displayName}</span>
        </div>
        <div className={styles.label}>
          <span className={styles.detailTitle}>Plan Name:</span>
          <span>{packageSubscription.planName}</span>
        </div>
        <div className={styles.label}>
          <span className={styles.detailTitle}>Validity:</span>
          <span>{packageSubscription.planValidity}</span>
        </div>
        <div className={styles.mrp}>
          <span className={styles.detailTitle}>MRP:</span>
          <span>{INR_SYMBOL + packageSubscription.mrp}</span>
        </div>
      </div>
    </div>
  );
};

const PackageSubscription = ({
  selectedPackageSubscriptions,
  onPackagesSelected,
  filters,
  searchTerm,
  isOrderTransfer,
  onFilterChange,
  onSearchTermChange,
  handleChangePackageSubscriptionSelection,
}) => {
  const {
    selectedGrade,
    selectedTargetExam,
    selectedVerticalId,
    selectedTenantId,
    selectedOfficeLocationId,
  } = filters;
  const appliedFilters = {
    exams: selectedTargetExam,
    grade: selectedGrade,
    verticalId: selectedVerticalId,
    tenantId: selectedTenantId,
  };

  const {
    isFetching,
    isPending: isPackageDetailsLoading,
    data: packageDetails,
    error: packageDetailsError,
    refetch,
  } = useFetchPackages(appliedFilters, false);

  const {
    data: verticalAndTenantIds,
    isPending: isVerticalAndTenantIdsLoading,
  } = useVerticalAndTenantsId();

  if (isVerticalAndTenantIdsLoading) {
    return <FullPageLoader />;
  }

  const filteredPackages = !!packageDetails
    ? packageDetails.filter((pkg) =>
        pkg.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const verticalMenuItems = verticalAndTenantIds?.map(({ id, name }) => ({
    key: id,
    value: id,
    label: name,
  }));

  const selectedVertical = verticalAndTenantIds?.find(
    (vertical) => vertical.id === selectedVerticalId
  );

  const tenantMenuItems =
    selectedVertical?.tenants?.map(({ state_id, name, id }) => ({
      key: state_id,
      value: id,
      label: name,
      state_id,
    })) || [];

  return (
    <div className={styles.mainContainer}>
      <div className={styles.packageContainer}>
        <div className={styles.verticalAndTenantIdContainer}>
          <SingleSelect
            name="vertical"
            value={selectedVerticalId}
            menuItems={verticalMenuItems}
            tag="Select a Vertical"
            disabled={true}
          />
          <SingleSelect
            name="tenant"
            value={selectedTenantId}
            handleChange={(event) =>
              handleTenantChange(event, onFilterChange, tenantMenuItems)
            }
            menuItems={tenantMenuItems}
            tag="Select a Center"
            disabled={isOrderTransfer}
          />
        </div>
        <div className={styles.dropdownContainer}>
            <MultipleSelect
              multiple
              name="grade"
              tag="Select Grade"
              value={selectedGrade}
              menuItems={GRADES}
              handleChange={(event) =>
                onFilterChange("selectedGrade", event.target.value)
              }
              showCheckbox={true}
            />
            <MultipleSelect
              multiple
              name="targetExam"
              tag="Select Target Exam"
              value={selectedTargetExam}
              menuItems={TARGET_EXAMS}
              handleChange={(event) =>
                onFilterChange("selectedTargetExam", event.target.value)
              }
              showCheckbox={true}
            />
          <div className={styles.packageDetailsButtonContainer}>
            <PrimaryButton
              disabled={
                !isSearchButtonEnabled(
                  selectedTargetExam,
                  selectedGrade,
                  selectedVerticalId,
                  selectedTenantId
                )
              }
              onClick={() => handleOnClickSearchButton(refetch)}
            >
              Search
            </PrimaryButton>
            <SecondaryButton
              onClick={() => handleOnClickRefreshButton(onFilterChange)}
            >
              Clear
            </SecondaryButton>
          </div>
        </div>
        <div className={styles.searchFieldContainer}>
          <SearchField
            placeholder="Search by package name"
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
          />
        </div>
        <div className={styles.packageDetailsContainer}>
          {isPackageDetailsLoading && isFetching ? (
            <Loader />
          ) : (
            <>
              {packageDetailsError && (
                <ErrorMessage>
                  Error: {packageDetailsError.message}
                </ErrorMessage>
              )}
              {filteredPackages.length > 0
                ? filteredPackages.map((packageSubscription) =>
                    renderPackageDetails(
                      packageSubscription,
                      selectedPackageSubscriptions,
                      onPackagesSelected,
                      filters
                    )
                  )
                : !packageDetailsError && (
                    <p>Select filters to search Package Subscriptions.</p>
                  )}
            </>
          )}
        </div>
      </div>
      <div className={styles.selectedPackageSubscriptionsContainer}>
        <SelectedPackageSubscriptions
          selectedPackageSubscriptions={selectedPackageSubscriptions}
          handleChangePackageSubscriptionSelection={
            handleChangePackageSubscriptionSelection
          }
        />
      </div>
    </div>
  );
};

export default PackageSubscription;
